import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { getPastInspectionsInfolist } from "../../api/BorrowerApi";
import { getMockPastInspectionsInfolist } from "../../api/mock/MockBorrowerApi";
import { formatDataProperties, handleErrorResponse } from "../../Helpers";
import { IPastInspectionsView } from "../interfaces/Borrower";
import { IErrorResponse, IProxyUser } from "../interfaces/Global";
import BCMenuBar from "../ui/BCMenuBar";
import BCMessage from "../ui/BCMessage";
import BCTable from "../ui/BCTable";
import BWExportButton from "./BWExportButton";
import BWSearchBox from "./BWSearchBox";
import "../../styles/borrower/vendor-form.scss";
import BCButton from "../ui/BCButton";
import BCFormInput from "../ui/BCFormInput";
import BCValidationMessage from "../ui/BCValidationMessage";
import { BorrowerConstants } from "../constants/BorrowerConstants";
import { Loader } from "semantic-ui-react";
import { FeatureFlags } from "../constants/LocalServerConstants";

interface IBWPastInspections {
  isGreaterThanMobile: boolean;
  isGreaterThanTablet: boolean;
  isSidebar: boolean;
  isMessage: boolean;
  setIsMessage: React.Dispatch<React.SetStateAction<boolean>>;
  errorResponse: IErrorResponse | undefined;
  setErrorResponse: any;
  proxyUser: IProxyUser | undefined;
}

const BWPastInspections = ({ ...props }: IBWPastInspections) => {
  const [isLoading, setIsLoading] = useState(false as boolean);
  const [startDate, setStartDate] = useState("" as string);
  const [endDate, setEndDate] = useState("" as string);
  const [invalidProps, setInvalidProps] = useState([] as any);
  const [pastInspectionsInfoData, setPastInspectionsInfoData] = useState([] as IPastInspectionsView[]);

  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleGetPastInspections = async () => {
    setInvalidProps([]);
    const notValidProperties = [] as any;
    if (startDate && (new Date(startDate).getTime() > new Date().getTime())) {
      notValidProperties.push("invalid-start-date");
    }

    if (endDate && startDate) {
      new Date(endDate).getTime() < new Date(startDate).getTime() ? notValidProperties.push("invalid-end-date") : setInvalidProps([]);
    }

    if (notValidProperties.length === 0) {
      setIsLoading(true);
      await getPastInspectionsInfoAysnc();
      setIsLoading(false);
    }
    else {
      setTimeout(() => { 
        setInvalidProps([...notValidProperties]);
      }, 50);
    }
  };

  const getPastInspectionsInfoAysnc = async () => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockPastInspectionsInfolist() : await getPastInspectionsInfolist(props.proxyUser && props.proxyUser.id, startDate, endDate);
      if (response && response.ok) {
        props.setIsMessage(false);
        const inspectionInfolist = await response.json();
        const inspectionViews = [] as IPastInspectionsView[];
        inspectionInfolist.forEach((inspection) => {
          inspectionViews.push(inspection as IPastInspectionsView);
        });

        if (inspectionViews.length > 0) {
          setPastInspectionsInfoData(inspectionViews);
        }
        else {
          props.setIsMessage(true);
          props.setErrorResponse({errorTitle: "Oops!", errorMessage: "It looks like no inspection was found in this time frame."} as IErrorResponse);
        }
      }
      else {
        await handleErrorResponse(response, props.setIsMessage, props.setErrorResponse);
      }
    }
    catch (error) {
      handleError();
    }
  };

  const propertyOfInspection = (name: keyof IPastInspectionsView) : string => name;
  const convertToTableFormat = (inspections : IPastInspectionsView[]) => {
    const result = [] as any;
    inspections.forEach((inspection) => {
      const resultItem = {} as any;
      Object.entries(inspection).forEach(([key, value]) => {
        if (key === propertyOfInspection("dateInspected")
        || key === propertyOfInspection("completedOn")
        || key === propertyOfInspection("requestedDate")
        || key === propertyOfInspection("scheduleDate")) {
          resultItem[key] = new Date(value).toLocaleDateString();
        }
        else if(!Object.values(BorrowerConstants.unUsedPastInspectionProperty).includes(key)){
          resultItem[key] = value;
        }
      });

      result.push(resultItem);
    });

    const formattedInspection = formatDataProperties(result, [
      {field: "tcname", action: "rename", name: "TC Name"}, 
      {field: "tcphoneNumber", action: "rename", name: "TC Phone Number"},
      {field: "dateInspected", action: "rename", name: "Inspected On"},
      {field: "loanNumber", action: "rename", name: "Loan #"}
    ]);
    return formattedInspection;
  };

  const CreatePastInspectionsReportBar = () => {
    return (
      <React.Fragment>
        <div className="calendar-input-wrapper">
          <div className="calendar-input-label"><label>Start Date:</label></div>
          <div className="calendar-input-field">
            <BCFormInput 
              inputCalendarClass="current-inspections-calendar"
              labelPosition="left"
              value={startDate}
              type="calendar"
              field="validThroughDate"
              handleFormChange={(value) => setStartDate(value)}
            />
          </div>
        </div>
        <div className="calendar-input-wrapper">
          <div className="calendar-input-label"><label>End Date:</label></div>
          <div className="calendar-input-field">
            <BCFormInput 
              inputCalendarClass="current-inspections-calendar"
              labelPosition="left"
              value={endDate}
              type="calendar"
              field="validThroughDate"
              handleFormChange={(value) => setEndDate(value)}
            />
          </div>
        </div>
        <BCButton handleOnClick={() => handleGetPastInspections()}>Create</BCButton>
      </React.Fragment>
    );
  };

  return (
    <div className={(props.isGreaterThanTablet && props.isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      <React.Fragment>
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={props.isMessage}
          transitionOnMount
          styles="full-width"
          onDismiss={() => props.setIsMessage(false)}
          errorResponse={props.errorResponse}
        />
        <BCMenuBar
          isGreaterThanMobile={props.isGreaterThanMobile}
          leftChildren={[<CreatePastInspectionsReportBar />, <BWExportButton />]}
          rightChildren={[<BWSearchBox />]}
        />
        <BCValidationMessage invalidProps={invalidProps} prop="startDate" propText="Please enter a start date." />
        <BCValidationMessage invalidProps={invalidProps} prop="invalid-start-date" propText="Start Date can not be a future day." />
        <BCValidationMessage invalidProps={invalidProps} prop="invalid-end-date" propText="Please select an end date at least one day after start day." />
        {isLoading ? <Loader active inline="centered" size="big" /> 
        : <React.Fragment>{pastInspectionsInfoData.length ? 
            <BCTable
              dataRules={[{ field: "Id", rule: "hide" }, { field: "Allow Edit Id", rule: "hide" }]}
              data={convertToTableFormat(pastInspectionsInfoData)}
              selectable
              overflowX
              overflowY
              dataSingleLine
              sortable={[
                "City",
                "State",
                "County",
                "Loan Draw", 
                "Inspection No", 
                "Inspection Status", 
                "Inspected On", 
                "Completed On", 
                "Property",
                "Project No",
                "Project Name",
                "Property Type",
                "Loan #",
                "Requested Date", 
                "Borrower",
                "Builder",
                "Inspection Company"
              ]}
              pagination={25}
            /> : <React.Fragment></React.Fragment>}
          </React.Fragment>
        }
      {FeatureFlags.IS_DEBUG && <React.Fragment>
        <button onClick={() => console.log(startDate)}>startDate</button>
        <button onClick={() => console.log(pastInspectionsInfoData)}>pastInspectionsInfoData</button>
      </React.Fragment>}
      </React.Fragment>
    </div>
  );
};

export default BWPastInspections;